import axios from "axios";
import getBlogCollectionUrl from "@Pages/Blog/utils/getBlogCollectionUrl";
import { BLOG_ARTICLES_COLLECTION, BLOG_REGION_COUNTRY_SLUGS } from "../settings";
import type { BlogRegion } from "../types";
import {
  type CmsBlogCountry,
  blogRegionFromCountrySlug,
  blogRegionsFromCountrySlugs,
  buildFallbackHomeRegionOrder,
} from "./blogRegionModel";

/**
 * CMS ↔ frontend mapping (blog revamp regions)
 *
 * Live regions = any published CMS country (not archived) with ≥1 published article.
 * `BLOG_REGION_COUNTRY_SLUGS` / `BLOG_REGIONS` are fallbacks for home ordering and
 * legacy URL slugs + Tolgee copy — not a cap on which countries can go live.
 */

/** Payload staging ignores `where[isArchived]` on countries — filter in app instead. */
const PUBLISHED_COUNTRIES_QUERY = "where[_status][equals]=published";

const ACTIVE_COUNTRIES_LIMIT = 50;

export type LiveBlogRegionsResult = {
  liveRegions: BlogRegion[];
  liveRegionSlugs: string[];
  liveCountrySlugs: string[];
  homeRegionOrder: string[];
  regionDescriptions: Record<string, string>;
  usesHomeRegionFallback: boolean;
  /** Display model keyed by CMS country slug (home + live region links). */
  regionsByCountrySlug: Record<string, BlogRegion>;
};

/** CMS `blogHomeOrder` first; tie-break uses legacy `BLOG_REGION_COUNTRY_SLUGS` then slug. */
export const compareBlogRegionCountries = (
  left: Pick<CmsBlogCountry, "slug" | "blogHomeOrder">,
  right: Pick<CmsBlogCountry, "slug" | "blogHomeOrder">,
): number => {
  const leftOrder = left.blogHomeOrder ?? Number.MAX_SAFE_INTEGER;
  const rightOrder = right.blogHomeOrder ?? Number.MAX_SAFE_INTEGER;
  if (leftOrder !== rightOrder) return leftOrder - rightOrder;

  const leftIndex = left.slug ? BLOG_REGION_COUNTRY_SLUGS.indexOf(left.slug) : -1;
  const rightIndex = right.slug ? BLOG_REGION_COUNTRY_SLUGS.indexOf(right.slug) : -1;
  const leftFallback = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
  const rightFallback = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;
  if (leftFallback !== rightFallback) return leftFallback - rightFallback;

  return (left.slug ?? "").localeCompare(right.slug ?? "");
};

const isShowOnBlogHome = (country: CmsBlogCountry): boolean => country.showOnBlogHome === true;

const isActiveBlogCountry = (country: CmsBlogCountry): boolean => country.isArchived !== true;

const toDescriptions = (countries: CmsBlogCountry[]) =>
  countries.reduce<Record<string, string>>((acc, country) => {
    if (country.slug && country.regionDescription?.trim()) {
      acc[country.slug] = country.regionDescription.trim();
    }
    return acc;
  }, {});

const cmsCountriesBySlug = (countries: CmsBlogCountry[]): Map<string, CmsBlogCountry> =>
  new Map(
    countries
      .filter((country): country is CmsBlogCountry & { slug: string } => !!country.slug)
      .map((country) => [country.slug, country]),
  );

export { buildFallbackHomeRegionOrder } from "./blogRegionModel";

export const resolveLiveBlogRegions = (
  activeCountries: CmsBlogCountry[],
  countrySlugsWithArticles: string[],
): LiveBlogRegionsResult => {
  const publishedActive = activeCountries.filter(
    (country) => country.slug && isActiveBlogCountry(country),
  );
  const cmsBySlug = cmsCountriesBySlug(publishedActive);

  const eligible = countrySlugsWithArticles
    .map((slug) => cmsBySlug.get(slug) ?? { slug })
    .filter((country) => country.slug && isActiveBlogCountry(country))
    .sort(compareBlogRegionCountries);

  const liveCountrySlugs = eligible
    .map((country) => country.slug)
    .filter((slug): slug is string => !!slug);
  const liveRegions = blogRegionsFromCountrySlugs(liveCountrySlugs, cmsBySlug);

  const cmsHomeCountries = eligible.filter(isShowOnBlogHome).sort(compareBlogRegionCountries);
  const cmsHomeRegionOrder = cmsHomeCountries
    .map((country) => country.slug)
    .filter((slug): slug is string => !!slug);

  const usesHomeRegionFallback = cmsHomeRegionOrder.length === 0;
  const homeRegionOrder = usesHomeRegionFallback
    ? buildFallbackHomeRegionOrder(liveCountrySlugs)
    : cmsHomeRegionOrder;

  const regionsByCountrySlug = homeRegionOrder.reduce<Record<string, BlogRegion>>((acc, slug) => {
    acc[slug] = blogRegionFromCountrySlug(slug, cmsBySlug.get(slug));
    return acc;
  }, {});

  liveCountrySlugs.forEach((slug) => {
    if (!regionsByCountrySlug[slug]) {
      regionsByCountrySlug[slug] = blogRegionFromCountrySlug(slug, cmsBySlug.get(slug));
    }
  });

  return {
    liveRegions,
    liveRegionSlugs: liveRegions.map((region) => region.slug),
    liveCountrySlugs,
    homeRegionOrder,
    regionDescriptions: toDescriptions(publishedActive),
    usesHomeRegionFallback,
    regionsByCountrySlug,
  };
};

type CmsCountryRelation = {
  slug?: string;
  isArchived?: boolean | null;
  _status?: string;
};

const buildCountryRegionProbeQuery = (countrySlug: string): string =>
  [
    `where[country.slug][equals]=${encodeURIComponent(countrySlug)}`,
    "where[_status][equals]=published",
    "where[isArchived][equals]=false",
    "select[id]=true",
    "select[slug]=true",
    "select[country]=true",
    "limit=1",
    "depth=1",
  ].join("&");

export type FetchLiveBlogRegionsOptions = {
  /** Defaults to all active CMS countries (blog home). Pass one slug for region PDP. */
  probeCountrySlugs?: readonly string[];
};

export const fetchPublishedBlogCountry = async (
  countrySlug: string,
): Promise<CmsBlogCountry | null> => {
  const { data } = await axios.get<{ docs?: CmsBlogCountry[] }>(
    `${getBlogCollectionUrl(
      "countries",
    )}?${PUBLISHED_COUNTRIES_QUERY}&where[slug][equals]=${encodeURIComponent(
      countrySlug,
    )}&depth=0&limit=1&select[slug]=true&select[name]=true&select[isArchived]=true&select[logo]=true`,
  );
  return data?.docs?.[0] ?? null;
};

/** Live region = published country (not archived) + ≥1 published post; countries REST wins on archive. */
export const isBlogCountryRegionLive = async (countrySlug: string): Promise<boolean> => {
  try {
    const countryDoc = await fetchPublishedBlogCountry(countrySlug);
    if (!countryDoc || countryDoc.isArchived === true) return false;

    const postsUrl = getBlogCollectionUrl(BLOG_ARTICLES_COLLECTION);
    const { data } = await axios.get<{ docs?: { country?: CmsCountryRelation | number | null }[] }>(
      `${postsUrl}?${buildCountryRegionProbeQuery(countrySlug)}`,
    );
    const post = data.docs?.[0];
    if (!post) return false;

    const { country } = post;
    if (!country || typeof country !== "object") return false;

    if (country.isArchived === true) return false;
    if (country._status && country._status !== "published") return false;
    return true;
  } catch {
    return false;
  }
};

const fetchActiveBlogCountries = async (): Promise<CmsBlogCountry[]> => {
  const { data } = await axios.get<{ docs?: CmsBlogCountry[] }>(
    `${getBlogCollectionUrl(
      "countries",
    )}?${PUBLISHED_COUNTRIES_QUERY}&sort=blogHomeOrder&depth=0&limit=${ACTIVE_COUNTRIES_LIMIT}&select[slug]=true&select[name]=true&select[isArchived]=true&select[blogHomeOrder]=true&select[showOnBlogHome]=true&select[regionDescription]=true&select[logo]=true`,
  );
  return (data?.docs || []).filter((country) => country.slug && isActiveBlogCountry(country));
};

const fetchCountrySlugsWithLiveRegions = async (
  probeCountrySlugs: readonly string[],
): Promise<string[]> => {
  const regionChecks = await Promise.all(
    probeCountrySlugs.map(async (slug) => ({
      slug,
      isLive: await isBlogCountryRegionLive(slug),
    })),
  );
  return regionChecks.filter((check) => check.isLive).map((check) => check.slug);
};

export const fetchLiveBlogRegions = async (
  options: FetchLiveBlogRegionsOptions = {},
): Promise<LiveBlogRegionsResult> => {
  const activeCountries = await fetchActiveBlogCountries().catch(() => [] as CmsBlogCountry[]);
  const probeCountrySlugs =
    options.probeCountrySlugs ??
    activeCountries.map((country) => country.slug).filter((slug): slug is string => !!slug);

  const countrySlugsWithArticles = await fetchCountrySlugsWithLiveRegions(probeCountrySlugs);

  return resolveLiveBlogRegions(activeCountries, countrySlugsWithArticles);
};

export const isLiveBlogRegionSlug = (
  slug: string | undefined,
  liveRegionSlugs: string[],
): boolean => !!slug && liveRegionSlugs.includes(slug);
